import React, { memo } from 'react';
import { useLanguage } from '../context/LanguageContext';

const METALS = [
    { name: 'Gold', style: { background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)' } },
    { name: 'Silver', style: { background: 'linear-gradient(135deg, #F3F4F6 0%, #9CA3AF 100%)' } },
    { name: 'Rose Gold', style: { background: 'linear-gradient(135deg, #E0C8C5 0%, #B76E79 100%)' } },
    { name: 'Platinum', style: { background: 'linear-gradient(135deg, #F8FAFC 0%, #64748B 100%)' } }
];

const FINISHES = ['Polished', 'Matte', 'Antique', 'Hammered'];

const PROCESSES = ['None', 'Handmade', 'CNC/Laser', 'Navishi', 'CAD/Casting'];

const JEWELRY_STYLES = ['None', 'Polki', 'Meena', 'Kundan', 'Rhodium'];

const GEMSTONES = [
    { name: 'Diamond', style: { background: 'radial-gradient(circle at 30% 30%, #E0F2FE, #7DD3FC)' } },
    { name: 'Ruby', style: { background: 'radial-gradient(circle at 30% 30%, #F87171, #B91C1C)' } },
    { name: 'Emerald', style: { background: 'radial-gradient(circle at 30% 30%, #34D399, #047857)' } },
    { name: 'Sapphire', style: { background: 'radial-gradient(circle at 30% 30%, #60A5FA, #1D4ED8)' } },
    { name: 'Pearl', style: { background: 'radial-gradient(circle at 30% 30%, #FAFAF9, #A8A29E)' } },
    { name: 'None', style: { background: 'transparent' }, isNone: true }
];

interface IngredientsSelectorProps {
    selectedMetal: string;
    setSelectedMetal: (m: string) => void;
    selectedFinish: string;
    setSelectedFinish: (f: string) => void;
    selectedProcess: string;
    setSelectedProcess: (p: string) => void;
    selectedStyle: string;
    setSelectedStyle: (s: string) => void;
    selectedGemstones: string[];
    setSelectedGemstones: (g: string[]) => void;
}

const IngredientsSelector: React.FC<IngredientsSelectorProps> = memo(({
    selectedMetal, setSelectedMetal,
    selectedFinish, setSelectedFinish,
    selectedProcess, setSelectedProcess,
    selectedStyle, setSelectedStyle,
    selectedGemstones, setSelectedGemstones
}) => {
    const { t } = useLanguage();

    const toggleGemstone = (gem: string) => {
        if (gem === 'None') {
            setSelectedGemstones(['None']);
            return;
        }
        let newSelection = selectedGemstones.filter(g => g !== 'None');
        if (newSelection.includes(gem)) {
            newSelection = newSelection.filter(g => g !== gem);
        } else {
            newSelection = [...newSelection, gem];
        }
        setSelectedGemstones(newSelection.length === 0 ? ['None'] : newSelection);
    };

    return (
        <div className="space-y-3 pt-2 border-t border-black/5 dark:border-white/5">
            {/* Metals - Horizontal Slider */}
            <div className="space-y-1.5">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">{t('lblMetal')}</label>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {METALS.map(m => (
                        <button 
                            key={m.name} 
                            onClick={() => setSelectedMetal(m.name)} 
                            className={`shrink-0 h-9 px-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${selectedMetal === m.name ? 'bg-slate-100 dark:bg-white/10 border-slate-300 dark:border-white/20' : 'border-transparent hover:bg-slate-50 dark:hover:bg-white/5'}`}
                            title={m.name}
                        >
                            <span className="w-3.5 h-3.5 rounded-full shadow-sm shrink-0 border border-black/5" style={m.style} />
                            <span className={`text-[10px] font-bold uppercase tracking-wider ${selectedMetal === m.name ? 'text-slate-900 dark:text-white' : 'text-slate-500'}`}>{m.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Finish - Horizontal Slider */}
            <div className="space-y-1.5">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">{t('lblFinish')}</label>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {FINISHES.map(f => (
                        <button 
                            key={f} 
                            onClick={() => setSelectedFinish(f)} 
                            className={`shrink-0 h-9 px-4 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all flex items-center justify-center text-center leading-none ${selectedFinish === f ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent' : 'border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {/* Manufacturing Process - Horizontal Slider */}
            <div className="space-y-1.5">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">Process</label>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {PROCESSES.map(p => (
                        <button 
                            key={p} 
                            onClick={() => setSelectedProcess(p)} 
                            className={`shrink-0 h-9 px-4 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all flex items-center justify-center text-center leading-none 
                            ${selectedProcess === p 
                                ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent shadow-sm' 
                                : 'border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            {/* Jewelry Style - Horizontal Slider */}
            <div className="space-y-1.5">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">Jewelry Style</label>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {JEWELRY_STYLES.map(s => (
                        <button 
                            key={s} 
                            onClick={() => setSelectedStyle(s)} 
                            className={`shrink-0 h-9 px-4 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all flex items-center justify-center text-center leading-none 
                            ${selectedStyle === s 
                                ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent shadow-sm' 
                                : 'border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>
            
            {/* Gemstones - Grid Layout */}
            <div className="space-y-1.5">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">{t('lblGemstones')}</label>
                <div className="grid grid-cols-3 gap-2">
                    {GEMSTONES.map(g => (
                        <button 
                            key={g.name} 
                            onClick={() => toggleGemstone(g.name)} 
                            className={`h-10 px-2 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all flex items-center justify-center gap-2
                            ${selectedGemstones.includes(g.name) 
                                ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent shadow-sm' 
                                : 'border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                        >
                            <span 
                                className={`w-3.5 h-3.5 rounded-full border border-black/10 shadow-sm shrink-0 ${g.isNone ? 'border-dashed border-slate-400' : ''}`}
                                style={g.style}
                            />
                            {g.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
});

export default IngredientsSelector;